import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { merchantApi } from '@/services/api'
import 'leaflet/dist/leaflet.css'
import '@/styles/map.css'
import { MapHeader, MerchantsMap, MerchantsList, ImageModal, ReviewsModal } from '@/components/mappage'

export function MerchantsMapPage() {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [minRating, setMinRating] = useState<number | null>(null)
  const [onlyPromotions, setOnlyPromotions] = useState(false)
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<any | null>(null)
  const [locationReviews, setLocationReviews] = useState<any[]>([])
  const [isLoadingReviews, setIsLoadingReviews] = useState(false)
  const [newRating, setNewRating] = useState<number>(0)
  const [newComment, setNewComment] = useState<string>('')
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          })
        },
        (error) => {
          console.error('Error getting location:', error)
        }
      )
    }
  }, [])

  // Fetch merchants
  const { data: merchants, isLoading, refetch } = useQuery(
    ['merchants', { search: searchTerm, category: selectedCategory, minRating, onlyPromotions, userLocation }],
    async () => {
      const params = {
        lat: userLocation?.lat,
        lng: userLocation?.lng,
        radius: userLocation ? 5 : undefined,
        search: searchTerm || undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        min_rating: minRating ?? undefined,
        promotions: onlyPromotions || undefined,
      }
      const res = await merchantApi.getMerchants(params)
      const raw = (res as any).data?.data ?? (res as any).data
      const data = Array.isArray(raw) ? raw : (raw?.results ?? [])
      return data
    },
    { enabled: true }
  )

  // Fetch categories
  const { data: categories } = useQuery(
    'merchant-categories',
    () => merchantApi.getCategories().then(res => {
      const raw = (res as any).data?.data ?? (res as any).data
      const data = Array.isArray(raw) ? raw : (raw?.results ?? [])
      return data
    })
  )

  const categoryOptions = useMemo(() => {
    const allOption = { id: 'all', name: t('merchants.all'), icon: '📍', color: '#6B7280', icon_url: null }
    if (!Array.isArray(categories) || categories.length === 0) return [allOption]
    return [
      allOption,
      ...categories.map((cat: any) => ({
        ...cat,
        icon_url: cat.icon_url || null,
        icon: cat.icon || '📍'
      }))
    ]
  }, [categories, t])

  const mapCenter = useMemo(() => {
    if (userLocation) return [userLocation.lat, userLocation.lng] as [number, number]
    return [0.3365, 6.7273] as [number, number]
  }, [userLocation])

  const mapZoom = useMemo(() => {
    return userLocation ? 15 : 12
  }, [userLocation])


  const handleReviewsClick = (merchant: any) => {
    setSelectedLocation(merchant)
    setReviewsModalOpen(true)
  }

  const handleRatingClick = (merchant: any) => {
    setSelectedLocation(merchant)
    setReviewsModalOpen(true)
  }

  // Fetch reviews when modal opens
  useEffect(() => {
    if (reviewsModalOpen && selectedLocation?.id) {
      setIsLoadingReviews(true)
      merchantApi.getLocationReviews(selectedLocation.id)
        .then((res) => {
          const list = (res as any).data?.data ?? (res as any).data
          const reviewsList = Array.isArray(list) ? list : (list?.results ?? [])
          setLocationReviews(reviewsList)
        })
        .catch((error) => {
          console.error('Failed to fetch reviews:', error)
          setLocationReviews([])
        })
        .finally(() => {
          setIsLoadingReviews(false)
        })
    }
  }, [reviewsModalOpen, selectedLocation?.id])

  const handleSubmitReview = async () => {
    if (!selectedLocation || newRating <= 0) return
    try {
      await merchantApi.createReview({ merchant_location: selectedLocation.id, rating: newRating, comment: newComment || undefined })
      const res = await merchantApi.getLocationReviews(selectedLocation.id)
      const list = (res as any).data?.data ?? (res as any).data
      const reviewsList = Array.isArray(list) ? list : (list?.results ?? [])
      setLocationReviews(reviewsList)
      setNewRating(0)
      setNewComment('')
    } catch (e) {
      console.error('Submit review failed:', e)
    }
  }

  return (
    <div className="min-h-screen bg-background-light">
      <MapHeader
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        categoryOptions={categoryOptions}
        minRating={minRating}
        onMinRatingChange={setMinRating}
        onlyPromotions={onlyPromotions}
        onPromotionsChange={setOnlyPromotions}
      />

      {viewMode === 'map' ? (
        <MerchantsMap
          center={mapCenter}
          zoom={mapZoom}
          userLocation={userLocation}
          merchants={merchants || []}
          onImageClick={(imageUrl) => {
            setSelectedImage(imageUrl)
            setImageModalOpen(true)
          }}
          onReviewsClick={handleReviewsClick}
          onRatingClick={handleRatingClick}
        />
      ) : (
        <MerchantsList
          merchants={merchants || []}
          isLoading={isLoading}
          searchTerm={searchTerm}
          selectedCategory={selectedCategory}
          onRefetch={refetch}
        />
      )}

      <ImageModal
        isOpen={imageModalOpen}
        imageUrl={selectedImage}
        onClose={() => setImageModalOpen(false)}
      />

      <ReviewsModal
        isOpen={reviewsModalOpen}
        location={selectedLocation}
        reviews={locationReviews}
        isLoadingReviews={isLoadingReviews}
        newRating={newRating}
        newComment={newComment}
        onClose={() => setReviewsModalOpen(false)}
        onRatingChange={setNewRating}
        onCommentChange={setNewComment}
        onSubmitReview={handleSubmitReview}
      />
    </div>
  )
}
